/**
 * ExHentai source module. Auto-registered by `src/sources/registry.ts`.
 *
 * Same shared logic as e-hentai, with three differences:
 *
 *   1. Host = `exhentai.org`, api = `s.exhentai.org/api.php`.
 *   2. `validateAuth` additionally requires the user's auth blob to
 *      include a non-empty `igneous` cookie. Missing `igneous` produces
 *      a Sad Panda response that we want to refuse up front.
 *   3. Different login UX copy — the user must log into the e-hentai
 *      forums first, THEN visit exhentai.org so the browser sets
 *      `igneous`, before copying cookies.
 *
 * Users who are only logged into e-hentai can paste just `ipb_member_id`
 * + `ipb_pass_hash` (or reuse their saved E-Hentai connection): `prepareAuth`
 * asks the worker's `/eh/exhentai-session` endpoint to run exhentai's
 * remoteapi redirect dance and returns the `igneous` cookie it issues.
 */

import { getProxyBase } from '@/lib/proxy';
import { serializeCookies } from '@/lib/cookies';
import {
  AuthExpired,
  CloudflareChallenge,
  NetworkError,
  RateLimited,
  SadPanda,
} from '@/lib/errors';
import { buildEhSource } from '../_eh-shared/build-source';
import { SITE_EX } from '../_eh-shared/urls';
import { createSessionHealer } from './session-heal';
import type { ComicSource, SourceAuth } from '../types';

const LOGIN_NOTES = `Recommended ExHentai steps:

1. Log in at https://forums.e-hentai.org/index.php?act=Login&CODE=01.
   Use the same browser/profile you will copy cookies from.

2. Open https://exhentai.org/ in that same browser.
   Continue only if you see a gallery list. If you see Sad Panda in the browser,
   Bunko cannot fix that with cookies.

3. Open DevTools -> Application/Storage -> Cookies and copy:

     Required:
       - ipb_member_id   (usually from e-hentai.org or forums.e-hentai.org)
       - ipb_pass_hash   (usually from e-hentai.org or forums.e-hentai.org)

     Recommended:
       - igneous         (from exhentai.org). If you leave it out, Bunko asks
                         exhentai for one using your e-hentai login.

     Optional/helpful:
       - sk              (if present)
       - cf_clearance    (best if it is from exhentai.org)
       - __cf_bm         (if present)

   A cf_clearance from e-hentai.org usually does not help ExHentai. Use the
   exhentai.org cf_clearance if your browser has one. It is also OK if your
   browser has no Cloudflare cookies.

4. Paste like this:

     ipb_member_id=...; ipb_pass_hash=...; igneous=...; sk=...

5. Click Save. Bunko validates by contacting exhentai.org through the proxy.
   If validation is blocked by a temporary worker/proxy IP ban, tick
   "Save without validating" to store the cookies anyway, then retry later.

Already connected E-Hentai? Use the "Use my E-Hentai login" button above
instead of pasting anything.

If Bunko says "Missing igneous", it can also mean igneous was present but stale,
invalid, or copied from a different browser/account session. Re-open exhentai.org,
confirm it still shows galleries, then copy ipb_member_id, ipb_pass_hash, and
igneous again from that same browser profile.`;

function usableIgneous(value: string | undefined): boolean {
  const trimmed = value?.trim().toLowerCase();
  return Boolean(trimmed && trimmed !== 'mystery');
}

const ehSource = buildEhSource({
  site: SITE_EX,
  sourceId: 'exhentai',
  name: 'ExHentai',
  description:
    'Browse, search, and read galleries from exhentai.org. Requires an e-hentai account with ExHentai access; Bunko can derive the "igneous" cookie from your e-hentai login.',
  loginNotes: LOGIN_NOTES,
  extraValidate: async (auth: SourceAuth) => {
    if (!usableIgneous(auth.cookies?.igneous)) {
      return {
        ok: false,
        reason:
          'exhentai requires a non-empty `igneous` cookie. Log into forums.e-hentai.org first, visit exhentai.org, then re-copy your cookies.',
      };
    }
    return { ok: true };
  },
});

const healer = createSessionHealer((auth) => acquireIgneous(auth));

// Every method that sends the saved cookies retries once with a freshly
// issued igneous when ExHentai answers with Sad Panda.
const source: ComicSource = {
  ...ehSource,
  search: healer.heal(ehSource.search, 1),
  getPopular: healer.heal(ehSource.getPopular, 1),
  getComic: healer.heal(ehSource.getComic, 1),
  getPages: healer.heal(ehSource.getPages, 1),
  resolveImageUrl: healer.heal(ehSource.resolveImageUrl, 1),
  ...(ehSource.getRecommendations && {
    getRecommendations: healer.heal(ehSource.getRecommendations, 1),
  }),
  ...(ehSource.getPagePreviews && { getPagePreviews: healer.heal(ehSource.getPagePreviews, 1) }),
  ...(ehSource.getComments && { getComments: healer.heal(ehSource.getComments, 1) }),
  ...(ehSource.validateAuth && { validateAuth: healer.heal(ehSource.validateAuth, 0) }),
  ...(ehSource.listFavorites && { listFavorites: healer.heal(ehSource.listFavorites, 0) }),
  ...(ehSource.addFavorite && { addFavorite: healer.heal(ehSource.addFavorite, 1) }),
  ...(ehSource.removeFavorite && { removeFavorite: healer.heal(ehSource.removeFavorite, 1) }),
  reconcileAuth: (auth) => healer.reconcile(auth),
  onAuthRefreshed: (listener) => healer.onRefreshed(listener),
  prepareAuth: async (auth) => {
    if (auth.anonymous || usableIgneous(auth.cookies?.igneous)) return auth;
    if (!auth.cookies?.ipb_member_id || !auth.cookies.ipb_pass_hash) return auth;
    const { cookies } = await acquireIgneous(auth);
    return { ...auth, cookies: { ...auth.cookies, ...cookies } };
  },
};

export default source;

interface ExhentaiSessionResponse {
  status: 'ok' | 'no-access' | 'logged-out' | 'challenge' | 'rate-limited' | 'error';
  cookies?: Record<string, string>;
  detail?: string;
}

/**
 * Exchange e-hentai login cookies (`ipb_member_id` + `ipb_pass_hash`) for an
 * ExHentai session. The worker follows exhentai's remoteapi redirect chain
 * with a private cookie jar and returns `igneous` (plus any refreshed
 * session cookies) in a JSON body — `/forward` strips `Set-Cookie`, so this
 * is the only way the SPA can see them.
 */
export async function acquireIgneous(
  auth: SourceAuth,
): Promise<{ cookies: Record<string, string> }> {
  const cookieHeader = serializeCookies(auth.cookies);
  let res: Response;
  try {
    res = await fetch(`${await getProxyBase()}/eh/exhentai-session`, {
      method: 'POST',
      headers: { 'X-Forward-Cookie': cookieHeader },
    });
  } catch (err) {
    throw new NetworkError('Could not reach the Bunko proxy to set up ExHentai.', {
      source: 'exhentai',
      cause: err,
    });
  }
  if (res.status === 429) {
    throw new RateLimited('Too many ExHentai setup attempts. Wait a few minutes.', {
      source: 'exhentai',
      egress: 'proxy',
    });
  }
  if (!res.ok) {
    throw new NetworkError(
      `ExHentai setup failed (HTTP ${res.status}). An older Bunko worker may need redeploying.`,
      { source: 'exhentai', status: res.status },
    );
  }
  const body = (await res.json()) as ExhentaiSessionResponse;
  const igneous = body.cookies?.igneous;
  if (body.status === 'ok' && igneous && usableIgneous(igneous)) {
    return { cookies: body.cookies ?? {} };
  }
  switch (body.status) {
    case 'no-access':
      throw new SadPanda(
        'ExHentai did not grant access to this e-hentai account (Sad Panda). New accounts need about a week, and the account must not be restricted. Open exhentai.org in your browser to confirm.',
        { source: 'exhentai' },
      );
    case 'logged-out':
      throw new AuthExpired(
        'e-hentai did not accept ipb_member_id / ipb_pass_hash. Log in again and re-copy them.',
        { source: 'exhentai' },
      );
    case 'challenge':
      throw new CloudflareChallenge(
        'Cloudflare challenged the proxy while setting up ExHentai. Retry, or paste igneous from your browser.',
        { source: 'exhentai' },
      );
    case 'rate-limited':
      throw new RateLimited('ExHentai temporarily banned the proxy IP during setup.', {
        source: 'exhentai',
        egress: 'proxy',
      });
    default:
      throw new NetworkError(
        `ExHentai setup did not return an igneous cookie${body.detail ? ` (${body.detail})` : ''}. Paste igneous from exhentai.org instead.`,
        { source: 'exhentai' },
      );
  }
}
